import { gsap } from 'gsap';
import Signal from '../../assets/scripts/classes/Signal';
import PageScroll from '../../assets/scripts/helpers/Scroll';

const HTML_CLASSLIST = document.documentElement.classList;

class Popups {
	constructor() {
		this.init();
	}

	init() {
		this.wrapper = document.querySelector('[data-popups]');

		if (!this.wrapper) {
			return;
		}

		this.onOpen = new Signal();
		this.onClose = new Signal();
		this.onCloseStart = new Signal();

		this.opened = false;
		this.openedClass = '';

		this.activePopup = null;
		this.activePopupName = '';
		this.popups = document.querySelectorAll('[data-popup]');

		const slideInAnimation = (popup, completeHandler) => {
			gsap.fromTo(
				popup,
				{ xPercent: 100, display: 'flex' },
				{
					duration: 0.7,
					xPercent: 0,
					clearProps: 'transform',
					onStart: () => {
						setTimeout(() => {
							this.wrapper.classList.add('_active');
						});
					},
					onComplete: () => {
						completeHandler();
					},
				},
			);
		};
		const slideOutAnimation = (popup, immediate, completeHandler) => {
			gsap.to(popup, {
				duration: immediate ? 0 : 0.7,
				xPercent: 100,
				display: 'none',
				clearProps: 'transform',
				onStart: () => {
					this.wrapper.classList.remove('_active');
				},
				onComplete: () => {
					completeHandler();
				},
			});
		};

		this.animations = {
			slideIn: slideInAnimation,
			slideOut: slideOutAnimation,
		};

		window.addEventListener('click', (e) => {
			const opener = e.target.closest('[data-popup-opener]');
			const isOpener = opener?.hasAttribute('data-popup-opener');

			if (isOpener) {
				e.preventDefault();
				this.open(opener.getAttribute('data-popup-opener'));
			}
		});

		Array.from(document.querySelectorAll('[data-popup-closer]')).forEach(
			(element) => {
				element.addEventListener('click', (e) => {
					e.target.tagName !== 'A' && e.preventDefault();

					this.close();
				});
			},
		);

		window.addEventListener('keydown', (e) => {
			if (this.opened && e.key === 'Escape') {
				this.close();
			}
		});

		let isMouseDownOnOverlay = false;

		this.wrapper.addEventListener('mousedown', (e) => {
			if (e.target === this.wrapper) {
				isMouseDownOnOverlay = true;
			} else {
				isMouseDownOnOverlay = false;
			}
		});

		this.wrapper.addEventListener('mouseup', (e) => {
			if (this.opened && isMouseDownOnOverlay && e.target === this.wrapper) {
				this.close();
			}
			isMouseDownOnOverlay = false;
		});
	}
	open(name) {
		if (this.activePopupName === name || this.animating) {
			return;
		}

		if (this.opened) {
			this.close(true);
		}

		const popup = this.wrapper.querySelector('[data-popup="' + name + '"]');

		if (!popup) {
			console.log('No popup for ' + name + ' opener');
			return;
		}

		this.opened = true;
		this.activePopup = popup;
		this.activePopupName = name;
		this.popupOpenAnimation = this.activePopup.getAttribute(
			'data-popup-open-animation',
		);

		this.wrapper.classList.add('_' + name);

		gsap.set(this.wrapper, { autoAlpha: 1 });
		gsap.to(this.wrapper, {
			duration: 0.7,
			overwrite: true,
			display: 'flex',
			onComplete: () => {
				this.wrapper.classList.remove('no-pe');
			},
		});

		const completeHandler = () => {
			const focusElement = this.activePopup.querySelector('[data-popup-focus]');

			if (focusElement) {
				focusElement.focus && focusElement.focus();
			}

			this.animating = false;
		};

		this.animating = true;
		if (this.animations[this.popupOpenAnimation]) {
			this.animations[this.popupOpenAnimation](
				this.activePopup,
				completeHandler,
			);
		} else {
			gsap.fromTo(
				this.activePopup,
				{ autoAlpha: 0, scale: 0.98, display: 'flex' },
				{
					duration: 0.7,
					autoAlpha: 1,
					scale: 1,
					clearProps: 'transform',
					onComplete: () => {
						completeHandler();
					},
				},
			);
		}

		HTML_CLASSLIST.add('_popup-opened');

		this.openedClass = '_popup-opened-' + name;
		HTML_CLASSLIST.add(this.openedClass);

		PageScroll.disabled();

		window.history.pushState({}, '', `#${name}`);

		this.onOpen.call();
	}
	close(immediate = false) {
		if (this.animating) {
			return;
		}

		if (this.opened) {
			this.opened = false;

			this.wrapper.classList.remove('_' + this.activePopupName);

			this.onCloseStart.call(this.activePopupName);
			this.popupCloseAnimation = this.activePopup.getAttribute(
				'data-popup-close-animation',
			);

			this.activePopupName = '';

			this.wrapper.classList.add('no-pe');
			gsap.to(this.wrapper, { duration: 0.7, display: 'none' });

			const completeHandler = () => {
				this.animating = false;
				this.onClose.call();
			};

			this.animating = true;
			if (this.animations[this.popupCloseAnimation]) {
				this.animations[this.popupCloseAnimation](
					this.activePopup,
					immediate,
					completeHandler,
				);
			} else {
				gsap.to(this.activePopup, {
					duration: immediate ? 0 : 0.7,
					autoAlpha: 0,
					scale: 0.98,
					display: 'none',
					clearProps: 'transform',
					onComplete: () => {
						completeHandler();
					},
				});
			}

			HTML_CLASSLIST.remove('_popup-opened');

			if (this.openedClass !== '') {
				HTML_CLASSLIST.remove(this.openedClass);
				this.openedClass = '';
			}

			PageScroll.enabled();

			window.history.pushState(
				'',
				document.title,
				window.location.pathname + window.location.search,
			);
		}
	}
	getPopup(name) {
		return this.wrapper.querySelector('[data-popup="' + name + '"]');
	}
}

export default new Popups();
